import React, { useEffect, useState } from "react";
import {
  DatePicker,
  TimePicker,
  Select,
  Button,
  Form,
  Typography,
  Card,
  Table,
  Modal,
  Tag,
} from "antd";
import {
  CalendarOutlined,
  ClockCircleOutlined,
  NumberOutlined,
  PlusOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import dataService from "../networks/api/service/dataService";
import { useNavigate } from "react-router-dom";
import CustomTable from "../components/TableComponent/CustomTable";
import colors from "../assets/styles/colour";

const { Title, Text } = Typography;
const { Option } = Select;

function AdminAppointment() {
  const [formData, setFormData] = useState({
    date: null,
    timeFrom: null,
    timeTo: null,
    noOfSlot: 1,
  });

  const [appointments, setAppointments] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const navigate = useNavigate();
  // Fetches appointments and sets table data
  const getAppoinmentDetails = async () => {
    try {
      const res = await dataService.getAdminAppointment();
      if (res.code === 200) {
        setAppointments(res.data);
      }
    } catch (e) {
      console.error("Error fetching admin appointments:", e);
    }
  };

  // Handles form submission
  const handleFormSubmit = () => {
    const payload = {
      date: formData.date ? formData.date.format("YYYY-MM-DD") : "",
      timeFrom: formData.timeFrom ? formData.timeFrom.format("hh:mm A") : "",
      timeTo: formData.timeTo ? formData.timeTo.format("hh:mm A") : "",
      noOfSlot: formData.noOfSlot,
    };
    console.log("Payload:", payload);
    addAdminAppointment(payload);
  };

  const addAdminAppointment = async (payload) => {
    try {
      const res = await dataService.AdminAppointment(payload);
      if (res.code === 200) {
        setModalVisible(false);
        getAppoinmentDetails(); // Refresh table after adding appointment
      }
    } catch (e) {
      console.error("Error adding admin appointment:", e);
    }
  };

  useEffect(() => {
    getAppoinmentDetails();
  }, []);

  const columns = [
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      render: (text) => new Date(text).toLocaleDateString(),
    },
    {
      title: "Time From",
      dataIndex: "timeFrom",
      key: "timeFrom",
    },
    {
      title: "Time To",
      dataIndex: "timeTo",
      key: "timeTo",
    },
    {
      title: "Slots",
      dataIndex: "slots",
      key: "slots",
      render: (slots) => (
        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
          {slots && slots.length > 0 ? (
            slots.map((slot, index) => (
              <Tag color="blue" key={index} style={{ fontSize: "12px" }}>
                {slot}
              </Tag>
            ))
          ) : (
            <Tag color="red">N/A</Tag>
          )}
        </div>
      ),
    },

    {
      title: "Status",
      dataIndex: "status",
      key: "status",
    },
    {
      title: "Actions",
      key: "actions",
      render: (text, record) => (
        <Button
          type="primary"
          icon={<EyeOutlined />}
          // onClick={() => navigate(`/login/appointment-details`)}
          onClick={() =>
            navigate(`/login/appointment-details`, {
              state: { appointment: record },
            })
          }
          style={{
            background: colors.sidebarText,
            borderColor: "#1890ff",
            color: "#fff",
            fontWeight: "bold",
            borderRadius: "8px",
            padding: "4px 12px",
          }}
        >
          View Details
        </Button>
      ),
    },
  ];

  return (
    <div style={{ padding: 20 }}>
      {/* Add Appointment Button */}
      <Button
        icon={<PlusOutlined />}
        onClick={() => setModalVisible(true)}
        style={{ marginBottom: 20, background: colors.button, color: "white" }}
      >
        Add Appointment
      </Button>

      {/* Appointments Table */}
      <CustomTable
        dataSource={appointments.map((item, index) => ({
          key: index,
          ...item,
        }))}
        columns={columns}
        bordered
        size="middle"
        style={{
          boxShadow: "0 4px 10px rgba(0, 0, 0, 0.1)",
          borderRadius: "8px",
          backgroundColor: "#fff",
          width: "100%",
          overflowX: "auto",
        }}
        scroll={{ x: 800 }}
      />

      {/* Add Appointment Modal */}
      <Modal
        title="Add Appointment"
        visible={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
      >
        <Form layout="vertical" onFinish={handleFormSubmit}>
          {/* Date Picker */}
          <Form.Item
            label={
              <>
                <CalendarOutlined style={{ marginRight: 8 }} />
                <b>Select Date</b>
              </>
            }
            required
          >
            <DatePicker
              onChange={(date) => setFormData({ ...formData, date })}
              style={{ width: "100%" }}
              format="YYYY-MM-DD"
            />
          </Form.Item>

          {/* Time From */}
          <Form.Item
            label={
              <>
                <ClockCircleOutlined style={{ marginRight: 8 }} />
                <b>Time From</b>
              </>
            }
            required
          >
            <TimePicker
              format="h:mm A"
              use12Hours
              onChange={(time) => setFormData({ ...formData, timeFrom: time })}
              style={{ width: "100%" }}
            />
          </Form.Item>

          {/* Time To */}
          <Form.Item
            label={
              <>
                <ClockCircleOutlined style={{ marginRight: 8 }} />
                <b>Time To</b>
              </>
            }
            required
          >
            <TimePicker
              format="h:mm A"
              use12Hours
              onChange={(time) => setFormData({ ...formData, timeTo: time })}
              style={{ width: "100%" }}
            />
          </Form.Item>

          {/* Number of Slots Dropdown */}
          <Form.Item
            label={
              <>
                <NumberOutlined style={{ marginRight: 8 }} />
                <b>Number of Slots</b>
              </>
            }
            required
          >
            <Select
              defaultValue="1"
              style={{ width: "100%" }}
              onChange={(value) =>
                setFormData({ ...formData, noOfSlot: value })
              }
            >
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((slot) => (
                <Option key={slot} value={slot}>
                  {slot}
                </Option>
              ))}
            </Select>
          </Form.Item>

          {/* Submit Button */}
          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              style={{
                width: "100%",
                backgroundColor: "#1890ff",
                borderColor: "#1890ff",
                fontWeight: "bold",
                height: 40,
              }}
            >
              Submit Appointment
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default AdminAppointment;
